import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Helper para acessar localStorage de forma segura
const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('⚠️ localStorage não disponível:', e.message);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('⚠️ localStorage não disponível:', e.message);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.warn('⚠️ localStorage não disponível:', e.message);
      return false;
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(safeLocalStorage.getItem('token'));
  const [sessionError, setSessionError] = useState(null);
  const fetchAttempts = useRef(0);
  const maxAttempts = 5;
  const isInitialMount = useRef(true);
  const profileFetchInProgress = useRef(false);
  const retryTimeoutRef = useRef(null);
  
  // Estado para controle de CPF obrigatório
  const [requiresCPFUpdate, setRequiresCPFUpdate] = useState(false);
  const [cpfUpdateUserInfo, setCpfUpdateUserInfo] = useState({ name: '', email: '' });
  
  // 🎵 VERSÃO ATUAL: Estado para controle de obras sem título
  const [worksNeedAttention, setWorksNeedAttention] = useState(0);
  const [showWorksAttentionModal, setShowWorksAttentionModal] = useState(false);

  // Função para validar se o token ainda é válido (não expirado)
  const isTokenValid = useCallback((tokenStr) => {
    if (!tokenStr) return false;
    try {
      const payload = JSON.parse(atob(tokenStr.split('.')[1]));
      const exp = payload.exp * 1000;
      const now = Date.now();
      const isValid = exp > now;
      
      if (!isValid) {
        console.warn('⚠️ Token expirado localmente');
      }
      return isValid;
    } catch (e) {
      console.error('Erro ao decodificar token:', e);
      return false;
    }
  }, []);

  // Função interna de logout
  const performLogout = useCallback(() => {
    console.log('🚪 Executando logout...');
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    safeLocalStorage.removeItem('token');
    safeLocalStorage.removeItem('mc_author_data');
    setToken(null);
    setUser(null);
    setSessionError(null);
    fetchAttempts.current = 0;
    profileFetchInProgress.current = false;
  }, []);

  // Função helper para fazer fetch com retry
  const fetchWithRetry = useCallback(async (fetchFn, maxRetries = 3, delay = 1000) => {
    let lastError = null;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        const result = await fetchFn();
        return result;
      } catch (error) {
        lastError = error;
        console.warn(`Tentativa ${i + 1}/${maxRetries} falhou:`, error.message);
        
        // Se for 401, não tentar novamente
        if (error.response?.status === 401) {
          throw error;
        }
        
        // Esperar antes de tentar novamente
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
        }
      }
    }
    
    throw lastError;
  }, []);

  const fetchProfile = useCallback(async (forceRefresh = false) => {
    // Evitar múltiplas chamadas simultâneas
    if (profileFetchInProgress.current && !forceRefresh) {
      console.log('🔄 Fetch de perfil já em andamento, ignorando...');
      return;
    }

    const currentToken = safeLocalStorage.getItem('token');
    
    if (!currentToken) {
      console.log('❌ Sem token, não é possível buscar perfil');
      setLoading(false);
      setUser(null);
      return;
    }

    // Verificar se token expirou localmente
    if (!isTokenValid(currentToken)) {
      console.warn('⚠️ Token expirado, fazendo logout local');
      performLogout();
      setLoading(false);
      return;
    }

    profileFetchInProgress.current = true;
    setSessionError(null);

    try {
      console.log('📡 Buscando perfil do usuário...');
      
      // Usar fetch com retry
      const response = await fetchWithRetry(
        () => authAPI.getProfile(),
        3,  // 3 tentativas
        1500  // 1.5s de delay entre tentativas
      );
      
      if (response.data) {
        console.log('✅ Perfil carregado com sucesso:', response.data.email);
        setUser(response.data);
        fetchAttempts.current = 0;
        setSessionError(null);
      }
    } catch (error) {
      console.error('❌ Erro ao buscar perfil após retries:', error);
      
      const status = error.response?.status;
      
      if (status === 401) {
        console.warn('🔒 Token inválido (401) - fazendo logout');
        setSessionError('Sessão expirada. Por favor, faça login novamente.');
        performLogout();
      } else if (status === 404) {
        console.warn('⚠️ Usuário não encontrado (404)');
        setSessionError('Usuário não encontrado.');
        performLogout();
      } else {
        // Para outros erros, NÃO fazer logout
        // Apenas mostrar mensagem e permitir retry manual
        console.warn('⚠️ Erro ao carregar perfil, mas mantendo token');
        setSessionError('Não foi possível carregar o perfil. Clique em "Tentar Novamente".');
      }
    } finally {
      profileFetchInProgress.current = false;
      setLoading(false);
    }
  }, [isTokenValid, performLogout, fetchWithRetry]);

  // Effect para carregar perfil quando token muda
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (token) {
        fetchProfile();
      } else {
        setLoading(false);
      }
    } else if (token) {
      fetchAttempts.current = 0;
      fetchProfile(true);
    }
  }, [token, fetchProfile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Login
  const login = async (email, password) => {
    try {
      console.log('🔐 Tentando login para:', email);
      setSessionError(null);
      setLoading(true);
      
      const response = await authAPI.login({ email, password });
      const { access_token, must_change_password, requires_cpf_update, user_name, user_email, works_need_attention } = response.data;
      
      console.log('✅ Login bem-sucedido, salvando token...');
      
      // 🎵 VERSÃO ATUAL: Verificar obras sem título
      if (works_need_attention && works_need_attention > 0) {
        console.log(`📝 Usuário tem ${works_need_attention} obra(s) sem título`);
        setWorksNeedAttention(works_need_attention);
        
        // Verificar se o aviso já foi pulado recentemente (últimas 24h)
        const skippedAt = safeLocalStorage.getItem('works_attention_skipped');
        if (skippedAt) {
          const skippedDate = new Date(skippedAt);
          const hoursSinceSkip = (Date.now() - skippedDate.getTime()) / (1000 * 60 * 60);
          if (hoursSinceSkip < 24) {
            console.log('⏭️ Aviso de obras sem título foi pulado há menos de 24h');
          } else {
            // Mostrar modal após carregar perfil
            setTimeout(() => setShowWorksAttentionModal(true), 2000);
          }
        } else {
          // Nunca foi pulado, mostrar após carregar perfil
          setTimeout(() => setShowWorksAttentionModal(true), 2000);
        }
      }
      
      // Salvar token
      safeLocalStorage.setItem('token', access_token);
      setToken(access_token);
      fetchAttempts.current = 0;
      
      // Check if user must change password (after recovery with temporary password)
      if (must_change_password) {
        console.log('⚠️ Usuário precisa trocar senha - redirecionando...');
        setLoading(false);
        return { ...response.data, must_change_password: true };
      }
      
      // ========================================
      // VERIFICAÇÃO DE CPF OBRIGATÓRIO
      // Se usuário não tem CPF, bloquear acesso
      // ========================================
      if (requires_cpf_update) {
        console.log('⚠️ Usuário precisa informar CPF - bloqueando acesso...');
        setRequiresCPFUpdate(true);
        setCpfUpdateUserInfo({ name: user_name || '', email: user_email || email });
        setLoading(false);
        return { ...response.data, requires_cpf_update: true };
      }
      
      // Buscar perfil imediatamente com retry
      profileFetchInProgress.current = false;
      
      try {
        console.log('📡 Buscando perfil após login...');
        const profileResponse = await fetchWithRetry(
          () => authAPI.getProfile(),
          3,
          1000
        );
        
        if (profileResponse.data) {
          console.log('✅ Perfil carregado após login:', profileResponse.data.email);
          setUser(profileResponse.data);
          setSessionError(null);
          
          // Verificar se o perfil indica falta de CPF
          if (!profileResponse.data.cpf_cnpj && !profileResponse.data.is_master) {
            console.log('⚠️ Perfil sem CPF - ativando modal de atualização');
            setRequiresCPFUpdate(true);
            setCpfUpdateUserInfo({ 
              name: profileResponse.data.name || '', 
              email: profileResponse.data.email || '' 
            });
          }
        }
      } catch (profileError) {
        console.warn('⚠️ Erro ao carregar perfil após login:', profileError);
        // Mesmo se falhar, o login foi bem-sucedido
        // O useEffect vai tentar novamente
        setSessionError('Carregando perfil...');
      }
      
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('❌ Erro no login:', error.response?.data || error.message);
      setLoading(false);
      throw error;
    }
  };
  
  // Função para marcar que o CPF foi atualizado
  const onCPFUpdated = useCallback(() => {
    setRequiresCPFUpdate(false);
    setCpfUpdateUserInfo({ name: '', email: '' });
    // Recarregar perfil para obter dados atualizados
    fetchProfile(true);
  }, []);

  // Registro
  const register = async (userData) => {
    try {
      console.log('📝 Registrando novo usuário:', userData.email);
      setSessionError(null);
      setLoading(true);
      
      const response = await authAPI.register(userData);
      const { access_token } = response.data;
      
      console.log('✅ Registro bem-sucedido, salvando token...');
      
      // Salvar token
      safeLocalStorage.setItem('token', access_token);
      setToken(access_token);
      fetchAttempts.current = 0;
      
      // Buscar perfil
      profileFetchInProgress.current = false;
      
      try {
        const profileResponse = await fetchWithRetry(
          () => authAPI.getProfile(),
          3,
          1000
        );
        
        if (profileResponse.data) {
          console.log('✅ Perfil carregado após registro:', profileResponse.data.email);
          setUser(profileResponse.data);
        }
      } catch (profileError) {
        console.warn('⚠️ Erro ao carregar perfil após registro:', profileError);
      }
      
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error('❌ Erro no registro:', error.response?.data || error.message);
      setLoading(false);
      throw error;
    }
  };

  // Logout público
  const logout = useCallback(() => {
    performLogout();
  }, [performLogout]);

  // Atualizar perfil
  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateProfile(data);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil:', error);
      throw error;
    }
  };

  // Verificar saúde da sessão
  const checkSession = useCallback(async () => {
    const currentToken = safeLocalStorage.getItem('token');
    if (!currentToken) return false;
    
    if (!isTokenValid(currentToken)) {
      return false;
    }
    
    try {
      await authAPI.getProfile();
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        return false;
      }
      return true;
    }
  }, [isTokenValid]);

  // Refresh manual do perfil
  const refreshProfile = useCallback(async () => {
    fetchAttempts.current = 0;
    setSessionError(null);
    profileFetchInProgress.current = false;
    await fetchProfile(true);
  }, [fetchProfile]);

  // 🔄 NOVO: Forçar atualização da sessão após pagamento/upgrade
  // Esta função é usada quando o admin aprova um pagamento
  // ou quando o usuário completa um upgrade de plano
  const forceRefreshSession = useCallback(async () => {
    try {
      console.log('🔄 Forçando atualização da sessão...');
      
      // Chamar endpoint de force-refresh
      const response = await authAPI.forceRefreshSession();
      
      if (response.data && response.data.success) {
        console.log('✅ Sessão atualizada com sucesso:', response.data.user);
        
        // Atualizar o estado do usuário com os dados mais recentes
        if (response.data.user) {
          // Fazer um fetch completo do perfil para garantir consistência
          await fetchProfile(true);
        }
        
        return { success: true, user: response.data.user };
      }
      
      return { success: false, error: 'Resposta inválida do servidor' };
    } catch (error) {
      console.error('❌ Erro ao forçar atualização da sessão:', error);
      
      // Se falhar, tentar fazer um refresh normal do perfil
      try {
        await fetchProfile(true);
        return { success: true, fallback: true };
      } catch (fallbackError) {
        console.error('❌ Fallback também falhou:', fallbackError);
        return { success: false, error: error.message };
      }
    }
  }, [fetchProfile]);

  // Limpar erro de sessão
  const clearSessionError = useCallback(() => {
    setSessionError(null);
  }, []);

  // Verificar se é admin/master
  const isAdmin = user?.plan === 'master' || user?.is_master === true || user?.status === 'master';

  // isAuthenticated - basta ter token válido
  const isAuthenticated = !!token && isTokenValid(token);
  
  // ========================================
  // VERIFICAÇÃO DE CPF BLOQUEANTE
  // Se o usuário não é master e não tem CPF, está bloqueado
  // ========================================
  const isCPFBlocked = isAuthenticated && user && !user.cpf_cnpj && !user.is_master;
  
  // Combinar ambas as verificações: estado requiresCPFUpdate OU verificação direta
  const needsCPFUpdate = requiresCPFUpdate || isCPFBlocked;
  
  // 🎵 VERSÃO ATUAL: Função para fechar modal de obras sem título
  const closeWorksAttentionModal = useCallback(() => {
    setShowWorksAttentionModal(false);
  }, []);
  
  // 🎵 VERSÃO ATUAL: Função para atualizar contagem de obras sem título
  const updateWorksNeedAttention = useCallback((count) => {
    setWorksNeedAttention(count);
    if (count === 0) {
      setShowWorksAttentionModal(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        logout,
        updateProfile,
        checkSession,
        refreshProfile,
        refreshUser: refreshProfile, // alias para componentes mais novos
        forceRefreshSession, // 🔄 NOVO: Para atualização forçada após pagamento
        clearSessionError,
        isAuthenticated,
        isAdmin,
        sessionError,
        // CPF obrigatório
        requiresCPFUpdate: needsCPFUpdate, // Usar verificação combinada
        cpfUpdateUserInfo,
        onCPFUpdated,
        isCPFBlocked, // Expor estado de bloqueio direto
        // 🎵 VERSÃO ATUAL: Obras sem título
        worksNeedAttention,
        showWorksAttentionModal,
        closeWorksAttentionModal,
        updateWorksNeedAttention,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
